import React, { useEffect, useMemo, useRef } from "react";
import { Box, Button, useTheme } from "@mui/material";
import { Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import WizardCst from "@components/WizardCst";
import {
  WizardHandle,
  WizardStep,
  WizardStepProps,
} from "@components/WizardCst/types";
import { useNavbarInitiative } from "@hooks/useNavbarInitiative";
import { LoTranslationProvider } from "../hooks/useLoTranslation";
import { AssociationItem } from "../AddActivity/types";
import { CourseWizardData } from "./types";
import {
  validateAdditionalInfo,
  validateAddActivities,
  validateAssociateInitiative,
  validateEditorial,
  validateMainInfo,
  validateTestsSurvey,
} from "./validation";
import MainInfoStep from "./steps/MainInfoStep";
import AdditionalInfoStep from "./steps/AdditionalInfoStep";
import AddActivitiesStep from "./steps/AddActivitiesStep";
import TestsSurveyStep from "./steps/TestsSurveyStep";
import AssociateInitiativeStep from "../AddActivity/steps/AssociateInitiativeStep";
import EditorialStep from "../AddActivity/steps/EditorialStep";

export interface CourseWizardProps {
  initialData: CourseWizardData;
  onComplete: (data: CourseWizardData) => void | Promise<void>;
  onSaveDraft?: (data: CourseWizardData) => void | Promise<void>;
  isSavingDraft?: boolean;
  isSubmitting?: boolean;
  mode?: "add" | "edit";
}

const CourseWizard: React.FC<CourseWizardProps> = ({
  initialData,
  onComplete,
  onSaveDraft,
  isSavingDraft,
  isSubmitting,
  mode = "add",
}) => {
  const { t } = useTranslation("wizard-course");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "", idCourse } = useParams();

  const catalogUrl = `/v2/corporate/${corporateId}/detail/catalog`;

  // TODO: anteprima learner del corso (modal/preview dedicato).
  // const handlePreview = () => {
  //   console.info("[course-wizard] vedi anteprima — TODO");
  // };

  // TODO: ripristinare il pulsante "Vedi anteprima" quando sara' disponibile la preview learner
  // const headerAction = (
  //   <Button
  //     onClick={handlePreview}
  //     variant="outlined"
  //     startIcon={<Eye size={16} />}
  //     sx={{
  //       textTransform: "none",
  //       fontWeight: 700,
  //       borderRadius: "8px",
  //       borderColor: theme.palette.divider,
  //       color: theme.palette.text.primary,
  //       flexShrink: 0,
  //     }}
  //   >
  //     {t("preview") || "Vedi anteprima"}
  //   </Button>
  // );

  const wizardRef = useRef<WizardHandle<CourseWizardData>>(null);
  const { selectedInitiative, selectedInitiativeName } = useNavbarInitiative();
  const prevNavbarRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const next = selectedInitiative;
    const prev = prevNavbarRef.current;
    if (next === prev) return;
    prevNavbarRef.current = next;

    wizardRef.current?.setData((current) => {
      let list = current.selected_initiatives;
      if (prev && prev !== "all") {
        const oldId = Number(prev);
        if (!Number.isNaN(oldId)) {
          list = list.filter((it) => Number(it.id) !== oldId);
        }
      }
      if (next && next !== "all") {
        const newId = Number(next);
        if (
          !Number.isNaN(newId) &&
          !list.some((it) => Number(it.id) === newId)
        ) {
          const item: AssociationItem = {
            id: newId,
            label: selectedInitiativeName,
          };
          list = [...list, item];
        }
      }
      return list === current.selected_initiatives
        ? {}
        : { selected_initiatives: list };
    });
  }, [selectedInitiative, selectedInitiativeName]);

  const isPublishedInitial = useMemo(() => {
    if (mode !== "edit") return false;
    const pd = initialData.publish_date;
    if (!pd) return false;
    const t = new Date(pd).getTime();
    if (Number.isNaN(t)) return false;
    return Date.now() > t;
  }, [mode, initialData.publish_date]);

  const isClosedInitial =
    mode === "edit" && !initialData.allow_post_publish_additions;

  const isEditMode = mode === "edit";

  const lockPublication = isPublishedInitial;
  const lockPropaedeutic = isEditMode;
  const lockCompletableToggle = isClosedInitial;
  const lockAdditions = isEditMode && isClosedInitial;
  const lockTestSurvey = isPublishedInitial && isClosedInitial;

  const MainInfoStepBound = useMemo(() => {
    const Bound: React.FC<WizardStepProps<CourseWizardData>> = (props) => (
      <MainInfoStep
        {...props}
        lockPublication={lockPublication}
        lockPropaedeutic={lockPropaedeutic}
        lockCompletableToggle={lockCompletableToggle}
      />
    );
    return Bound;
  }, [lockPublication, lockPropaedeutic, lockCompletableToggle]);

  const AddActivitiesStepBound = useMemo(() => {
    const Bound: React.FC<WizardStepProps<CourseWizardData>> = (props) => (
      <AddActivitiesStep {...props} lockAdditions={lockAdditions} />
    );
    return Bound;
  }, [lockAdditions]);

  const TestsSurveyStepBound = useMemo(() => {
    const Bound: React.FC<WizardStepProps<CourseWizardData>> = (props) => (
      <TestsSurveyStep {...props} lockTestSurvey={lockTestSurvey} />
    );
    return Bound;
  }, [lockTestSurvey]);

  const steps = useMemo<WizardStep<CourseWizardData>[]>(
    () => [
      {
        key: "main-info",
        label: t("steps.main-info.label"),
        Component: MainInfoStepBound,
        validate: validateMainInfo,
      },
      {
        key: "additional-info",
        label: t("steps.additional-info.label"),
        Component: AdditionalInfoStep,
        validate: validateAdditionalInfo,
      },
      {
        key: "add-activities",
        label: t("steps.add-activities.label"),
        Component: AddActivitiesStepBound,
        validate: validateAddActivities,
      },
      {
        key: "tests-survey",
        label: t("steps.tests-survey.label"),
        Component: TestsSurveyStepBound,
        validate: validateTestsSurvey,
      },
      {
        key: "associate-initiative",
        label: t("steps.associate-initiative.label"),
        Component: AssociateInitiativeStep,
        validate: validateAssociateInitiative,
      },
      {
        key: "editorial",
        label: t("steps.editorial.label"),
        Component: EditorialStep,
        validate: validateEditorial,
      },
    ],
    [t],
  );

  return (
    <AnimationFadeIn>
      <Box sx={{ py: 3 }}>
        <LoTranslationProvider loId={isEditMode ? idCourse : undefined}>
          <WizardCst<CourseWizardData>
            ref={wizardRef}
            title={mode === "edit" ? t("title-edit") : t("title")}
            breadcrumb={<Breadcrumb />}
            backLink={{
              label: t("back-link"),
              to: catalogUrl,
            }}
            steps={steps}
            initialData={initialData}
            onComplete={onComplete}
            onCancel={() => navigate(catalogUrl)}
            abandonMode={isEditMode ? "edit" : "create"}
            resetConfirm={{
              title: t("reset-confirm.title"),
              description: t("reset-confirm.description"),
              confirmLabel: t("reset-confirm.confirm"),
              cancelLabel: t("reset-confirm.cancel"),
            }}
            nextLabel={t("next") || "Prosegui"}
            resetLabel={t("reset") || "Reset"}
            completeLabel={t("publish") || "Pubblica"}
            backLabel={t("back") || "Indietro"}
            secondaryAction={
              onSaveDraft
                ? {
                    label: t("save-draft") || "Salva bozza",
                    onClick: onSaveDraft,
                    disabled: !!isSavingDraft,
                  }
                : undefined
            }
            isCompleting={isSubmitting}
          />
        </LoTranslationProvider>
      </Box>
    </AnimationFadeIn>
  );
};

export default CourseWizard;
